import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import { i18n } from "../../translate/i18n";
import {
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    FormControlLabel,
    Checkbox
} from "@material-ui/core";
import {
    Visibility,
    VisibilityOff,
    PlayArrow,
    Stop,
    GetApp,
    VolumeUp,
    ExpandMore,
    Add,
    Delete,
    Info,
    Chat,
    Business,
    Person,
    LocalOffer,
    ViewColumn,
    Event,
    AttachFile
} from "@material-ui/icons";
import { InputAdornment, IconButton } from "@material-ui/core";
import QueueSelectSingle from "../../components/QueueSelectSingle";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useTtsPreview } from "../../hooks/useTtsPreview";

const azureVoices = [
  "pt-BR-FabioNeural", "pt-BR-FranciscaNeural", "pt-BR-AntonioNeural",
  "pt-BR-BrendaNeural", "pt-BR-DonatoNeural", "pt-BR-ElzaNeural",
  "pt-BR-GiovannaNeural", "pt-BR-HumbertoNeural", "pt-BR-JulioNeural",
  "pt-BR-LeilaNeural", "pt-BR-LeticiaNeural", "pt-BR-ManuelaNeural",
  "pt-BR-NicolauNeural", "pt-BR-ValerioNeural", "pt-BR-YaraNeural"
];

const openAiTtsVoices = [
  "alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer",
  "ballad", "verse", "marin", "cedar"
];

const openAiTtsModels = [
  { value: "tts-1", label: "OpenAI TTS (rápido)" },
  { value: "tts-1-hd", label: "OpenAI TTS HD (qualidade)" },
  { value: "gpt-4o-mini-tts", label: "GPT-4o mini TTS" }
];

const openAiVoiceProfiles = {
  alloy: { gender: "Neutra", tags: ["equilibrada", "clara", "comercial"] },
  ash: { gender: "Masculina", tags: ["grave", "séria", "consultiva"] },
  coral: { gender: "Feminina", tags: ["calma", "acolhedora", "suave"] },
  echo: { gender: "Masculina", tags: ["firme", "objetiva", "profissional"] },
  fable: { gender: "Neutra", tags: ["expressiva", "narrativa", "energética"] },
  nova: { gender: "Feminina", tags: ["moderna", "leve", "comercial"] },
  onyx: { gender: "Masculina", tags: ["profunda", "marcante", "institucional"] },
  sage: { gender: "Neutra", tags: ["calma", "didática", "natural"] },
  shimmer: { gender: "Feminina", tags: ["brilhante", "amistosa", "leve"] },
  ballad: { gender: "Neutra", tags: ["calma", "suave", "GPT-4o mini"] },
  verse: { gender: "Neutra", tags: ["fluida", "moderna", "GPT-4o mini"] },
  marin: { gender: "Feminina", tags: ["serena", "elegante", "GPT-4o mini"] },
  cedar: { gender: "Masculina", tags: ["estável", "sóbria", "GPT-4o mini"] }
};

const azureVoiceProfiles = {
  "pt-BR-FabioNeural": { gender: "Masculina", tags: ["clássica", "comercial", "objetiva"] },
  "pt-BR-FranciscaNeural": { gender: "Feminina", tags: ["acolhedora", "clara", "natural"] },
  "pt-BR-AntonioNeural": { gender: "Masculina", tags: ["institucional", "firme", "grave"] },
  "pt-BR-BrendaNeural": { gender: "Feminina", tags: ["moderna", "leve", "energética"] },
  "pt-BR-DonatoNeural": { gender: "Masculina", tags: ["segura", "séria", "consultiva"] },
  "pt-BR-ElzaNeural": { gender: "Feminina", tags: ["calma", "didática", "profissional"] },
  "pt-BR-GiovannaNeural": { gender: "Feminina", tags: ["suave", "amistosa", "comercial"] },
  "pt-BR-HumbertoNeural": { gender: "Masculina", tags: ["natural", "equilibrada", "corporativa"] },
  "pt-BR-JulioNeural": { gender: "Masculina", tags: ["dinâmica", "clara", "moderna"] },
  "pt-BR-LeilaNeural": { gender: "Feminina", tags: ["serena", "consultiva", "elegante"] },
  "pt-BR-LeticiaNeural": { gender: "Feminina", tags: ["jovial", "nítida", "leve"] },
  "pt-BR-ManuelaNeural": { gender: "Feminina", tags: ["acolhedora", "calma", "empática"] },
  "pt-BR-NicolauNeural": { gender: "Masculina", tags: ["sóbria", "grave", "segura"] },
  "pt-BR-ValerioNeural": { gender: "Masculina", tags: ["comercial", "energética", "clara"] },
  "pt-BR-YaraNeural": { gender: "Feminina", tags: ["natural", "equilibrada", "profissional"] }
};

const inferTtsProvider = (voice, ttsProvider) => {
  if (ttsProvider === "azure" || ttsProvider === "openai") return ttsProvider;
  if (azureVoices.includes(voice)) return "azure";
  if (openAiTtsVoices.includes(voice)) return "openai";
  return "azure";
};

const getVoiceLabel = (voice) => {
  if (voice === "texto") return "Texto";
  if (azureVoices.includes(voice)) return voice.replace("pt-BR-", "").replace("Neural", "");
  return voice.charAt(0).toUpperCase() + voice.slice(1);
};

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },

    btnWrapper: {
        position: "relative",
    },

    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    colorAdorment: {
        width: 20,
        height: 20,
    },
    voicePreviewBox: {
        marginTop: theme.spacing(1.5),
        padding: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.grey[50]
    },
    voicePreviewActions: {
        display: "flex",
        gap: theme.spacing(1),
        alignItems: "center",
        flexWrap: "wrap",
        marginTop: theme.spacing(1)
    },
    voiceCatalog: {
        marginTop: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        overflow: "hidden"
    },
    voiceCatalogHeader: {
        display: "grid",
        gridTemplateColumns: "minmax(130px, 160px) minmax(100px, 120px) 1fr auto",
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        backgroundColor: theme.palette.grey[100],
        fontWeight: 700,
        fontSize: "0.8rem"
    },
    voiceCatalogRow: {
        display: "grid",
        gridTemplateColumns: "minmax(130px, 160px) minmax(100px, 120px) 1fr auto",
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        alignItems: "center",
        borderTop: `1px solid ${theme.palette.divider}`,
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
        }
    },
    voiceTags: {
        display: "flex",
        gap: theme.spacing(0.5),
        flexWrap: "wrap"
    },
    voiceTagChip: {
        height: 22,
        fontSize: "0.72rem"
    },
    accordion: {
        marginBottom: theme.spacing(1),
        "&:before": {
            display: "none",
        }
    },
    accordionSummary: {
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.spacing(0.5),
        minHeight: 48,
        "&.Mui-expanded": {
            minHeight: 48,
        }
    },
    accordionDetails: {
        padding: theme.spacing(2),
        flexDirection: "column",
        display: "flex",
        gap: theme.spacing(1)
    },
    transferRow: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-start",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(0.5)
    },
    selectRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1)
    },
    sectionTitle: {
        fontWeight: "bold",
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(1),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1)
    }
}));

const PromptSchema = Yup.object().shape({
    name: Yup.string().min(5, "Muito curto!").max(100, "Muito longo!").required("Obrigatório"),
    prompt: Yup.string().min(50, "Muito curto!").required("Descreva o treinamento para Inteligência Artificial"),
    voice: Yup.string().required("Informe o modo para Voz"),
    maxTokens: Yup.number().required("Informe o número máximo de tokens"),
    temperature: Yup.number().required("Informe a temperatura"),
    apiKey: Yup.string().required("Informe a API Key"),
    queueId: Yup.number().required("Informe a fila"),
    maxMessages: Yup.number().required("Informe o número máximo de mensagens"),
    voiceKey: Yup.string().when(["voice", "ttsProvider"], {
        is: (voice, ttsProvider) => voice !== "texto" && (ttsProvider || "azure") === "azure",
        then: Yup.string().required("Voice Key é obrigatória para síntese de voz Azure"),
        otherwise: Yup.string().notRequired()
    }),
    voiceRegion: Yup.string().when(["voice", "ttsProvider"], {
        is: (voice, ttsProvider) => voice !== "texto" && (ttsProvider || "azure") === "azure",
        then: Yup.string().required("Voice Region é obrigatória para síntese de voz Azure"),
        otherwise: Yup.string().notRequired()
    })
});

const PromptModal = ({ open, onClose, promptId }) => {
    const classes = useStyles();
    const [showApiKey, setShowApiKey] = useState(false);
    const [previewText, setPreviewText] = useState("Olá, aqui é um teste de voz do atendimento. Como posso te ajudar hoje?");
    const [previewLoadingVoice, setPreviewLoadingVoice] = useState("");
    const { playPreview, downloadPreview, stopPreview, releasePreview } = useTtsPreview();

    const handleToggleApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    const initialState = {
        name: "",
        prompt: "",
        voice: "texto",
        voiceKey: "",
        voiceRegion: "",
        ttsProvider: "azure",
        ttsModel: "tts-1",
        openaiTtsInstructions: "",
        ttsSpeed: 1,
        maxTokens: 100,
        temperature: 1,
        apiKey: "",
        queueId: null,
        maxMessages: 10,
        delay: 7,
        departmentTransfers: [],
        userTransfers: [],
        tagsAllowedForAI: [],
        lanesAllowedForAI: [],
        googleCalendarIntegrationId: 0,
        queueIdAfterCalendarCreate: 0,
        queueIdAfterCalendarUpdate: 0,
        queueIdAfterCalendarCancel: 0,
        sendAudioTranscriptionToContact: false,
        sendLinksAsTextWhenVoice: false,
        assetsAllowedForAI: [],
        maxAttachmentsPerReply: 2
    };

    const [prompt, setPrompt] = useState(initialState);
    const [googleCalendarList, setGoogleCalendarList] = useState([]);
    const [queues, setQueues] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [tagsList, setTagsList] = useState([]);
    const [lanesList, setLanesList] = useState([]);
    const [assetsPicker, setAssetsPicker] = useState([]);

    useEffect(() => {
        const fetchPrompt = async () => {
            if (!promptId) {
                setPrompt(initialState);
                return;
            }
            try {
                const { data } = await api.get(`/prompt/${promptId}`);
                setPrompt(prevState => ({
                    ...prevState,
                    ...data,
                    delay: data.delay ?? 7,
                    ttsProvider: data.ttsProvider || inferTtsProvider(data.voice, data.ttsProvider),
                    ttsModel: data.ttsModel || "tts-1",
                    openaiTtsInstructions: data.openaiTtsInstructions || "",
                    ttsSpeed: Number(data.ttsSpeed) || 1,
                    departmentTransfers: Array.isArray(data.departmentTransfers)
                        ? data.departmentTransfers.map((d) => ({
                            queueId: Number(d.queueId) || 0,
                            name: d.name || "",
                            keywords: Array.isArray(d.keywords) ? d.keywords.join(", ") : (d.keywords || "")
                        }))
                        : [],
                    userTransfers: Array.isArray(data.userTransfers)
                        ? data.userTransfers.map((u) => ({
                            userId: Number(u.userId) || 0,
                            name: u.name || "",
                            queueId: Number(u.queueId) || 0,
                            keywords: Array.isArray(u.keywords) ? u.keywords.join(", ") : (u.keywords || "")
                        }))
                        : [],
                    tagsAllowedForAI: Array.isArray(data.tagsAllowedForAI) ? data.tagsAllowedForAI : [],
                    lanesAllowedForAI: Array.isArray(data.lanesAllowedForAI) ? data.lanesAllowedForAI : [],
                    googleCalendarIntegrationId: Number(data.googleCalendarIntegrationId) > 0
                        ? Number(data.googleCalendarIntegrationId)
                        : 0,
                    queueIdAfterCalendarCreate: Number(data.queueIdAfterCalendarCreate) || 0,
                    queueIdAfterCalendarUpdate: Number(data.queueIdAfterCalendarUpdate) || 0,
                    queueIdAfterCalendarCancel: Number(data.queueIdAfterCalendarCancel) || 0,
                    sendAudioTranscriptionToContact: Boolean(data.sendAudioTranscriptionToContact),
                    sendLinksAsTextWhenVoice: Boolean(data.sendLinksAsTextWhenVoice),
                    assetsAllowedForAI: Array.isArray(data.assetsAllowedForAI) ? data.assetsAllowedForAI : [],
                    maxAttachmentsPerReply: Number(data.maxAttachmentsPerReply) > 0 ? Number(data.maxAttachmentsPerReply) : 2
                }));
            } catch (err) {
                toastError(err);
            }
        };

        fetchPrompt();
    }, [promptId, open]);

    useEffect(() => {
        if (open) {
            api.get("/queue").then(({ data }) => setQueues(Array.isArray(data) ? data : [])).catch(() => setQueues([]));
            api.get("/users", { params: { showAll: true } }).then(({ data }) => setUsersList(Array.isArray(data?.users) ? data.users : [])).catch(() => setUsersList([]));
            api.get("/tags/list").then(({ data }) => setTagsList(Array.isArray(data) ? data : [])).catch(() => setTagsList([]));
            api.get("/tag/kanban").then(({ data }) => setLanesList(data?.lista || [])).catch(() => setLanesList([]));
            api.get("/google-calendar-integrations").then(({ data }) => setGoogleCalendarList(Array.isArray(data) ? data : [])).catch(() => setGoogleCalendarList([]));
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const params = {};
        if (prompt.queueId) params.queueId = prompt.queueId;
        if (promptId) params.promptId = promptId;
        api.get("/ai-assets/picker", { params })
            .then(({ data }) => setAssetsPicker(Array.isArray(data) ? data : []))
            .catch(() => setAssetsPicker([]));
    }, [open, prompt.queueId, promptId]);

    useEffect(() => {
        // Quando abrir o modal, manter uma frase default para facilitar testes rápidos
        if (open) {
            setPreviewText("Olá, aqui é um teste de voz do atendimento. Como posso te ajudar hoje?");
        }
    }, [open]);

    const handleClose = () => {
        releasePreview();
        setPrompt(initialState);
        onClose();
    };

    const handleSavePrompt = async values => {
        const departmentTransfers = (values.departmentTransfers || [])
            .filter((d) => d && Number(d.queueId) > 0 && d.name)
            .map((d) => ({
                queueId: Number(d.queueId),
                name: String(d.name).trim(),
                keywords: typeof d.keywords === "string"
                    ? d.keywords.split(",").map((s) => s.trim()).filter(Boolean)
                    : (Array.isArray(d.keywords) ? d.keywords : [])
            }));
        const userTransfers = (values.userTransfers || [])
            .filter((u) => u && Number(u.userId) > 0 && Number(u.queueId) > 0 && u.name)
            .map((u) => ({
                userId: Number(u.userId),
                name: String(u.name).trim(),
                queueId: Number(u.queueId),
                keywords: typeof u.keywords === "string"
                    ? u.keywords.split(",").map((s) => s.trim()).filter(Boolean)
                    : (Array.isArray(u.keywords) ? u.keywords : [])
            }));
        const tagsAllowedForAI = (values.tagsAllowedForAI || [])
            .filter((t) => t && Number(t.tagId) > 0 && t.name)
            .map((t) => ({ tagId: Number(t.tagId), name: String(t.name).trim() }));
        const lanesAllowedForAI = (values.lanesAllowedForAI || [])
            .filter((l) => l && Number(l.tagId) > 0 && l.name)
            .map((l) => ({ tagId: Number(l.tagId), name: String(l.name).trim() }));
        const assetsAllowedForAI = (values.assetsAllowedForAI || [])
            .filter((a) => a && Number(a.assetId) > 0 && a.name)
            .map((a) => ({ assetId: Number(a.assetId), name: String(a.name).trim() }));

        const promptData = {
            ...values,
            queueId: Number(values.queueId) || 0,
            googleCalendarIntegrationId: Number(values.googleCalendarIntegrationId) > 0
                ? Number(values.googleCalendarIntegrationId)
                : 0,
            queueIdAfterCalendarCreate: Number(values.queueIdAfterCalendarCreate) || 0,
            queueIdAfterCalendarUpdate: Number(values.queueIdAfterCalendarUpdate) || 0,
            queueIdAfterCalendarCancel: Number(values.queueIdAfterCalendarCancel) || 0,
            departmentTransfers,
            userTransfers,
            tagsAllowedForAI,
            lanesAllowedForAI,
            assetsAllowedForAI,
            maxAttachmentsPerReply: Number(values.maxAttachmentsPerReply) > 0 ? Number(values.maxAttachmentsPerReply) : 2,
            sendLinksAsTextWhenVoice: Boolean(values.sendLinksAsTextWhenVoice)
        };
        if (!values.queueId) {
            toastError("Informe o setor");
            return;
        }
        try {
            if (promptId) {
                await api.put(`/prompt/${promptId}`, promptData);
            } else {
                await api.post("/prompt", promptData);
            }
            toast.success(i18n.t("promptModal.success"));
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                scroll="paper"
                fullWidth
            >
                <DialogTitle id="form-dialog-title">
                    {promptId
                        ? `${i18n.t("promptModal.title.edit")}`
                        : `${i18n.t("promptModal.title.add")}`}
                </DialogTitle>
                <Formik
                    initialValues={prompt}
                    enableReinitialize={true}
                    validationSchema={PromptSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSavePrompt(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values, setFieldValue }) => (
                        <Form style={{ width: "100%" }}>
                            <DialogContent dividers>
                                <Field
                                    as={TextField}
                                    label={i18n.t("promptModal.form.name")}
                                    name="name"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    required
                                />
                                <FormControl fullWidth margin="dense" variant="outlined">
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.apikey")}
                                        name="apiKey"
                                        type={showApiKey ? 'text' : 'password'}
                                        error={touched.apiKey && Boolean(errors.apiKey)}
                                        helperText={touched.apiKey && errors.apiKey}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        required
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleToggleApiKey}>
                                                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </FormControl>
                                <Field
                                    as={TextField}
                                    label={i18n.t("promptModal.form.prompt")}
                                    name="prompt"
                                    error={touched.prompt && Boolean(errors.prompt)}
                                    helperText={touched.prompt && errors.prompt}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    required
                                    rows={10}
                                    multiline={true}
                                />
                                <QueueSelectSingle />

                                <Accordion className={classes.accordion}>
                                    <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                                        <Typography className={classes.sectionTitle}>
                                            <Event />
                                            Google Calendar (opcional)
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails className={classes.accordionDetails}>
                                        <Typography variant="body2" color="textSecondary">
                                            Vincule uma integração para o agente listar, criar, atualizar e remover eventos.
                                            Conclua o OAuth no Google após criar a integração.
                                            {googleCalendarList[0]?.redirectUri && (
                                                <> Redirect no Google Cloud Console: <code>{googleCalendarList[0].redirectUri}</code></>
                                            )}
                                        </Typography>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>Integração Google Calendar</InputLabel>
                                            <Field as={Select} label="Integração Google Calendar" name="googleCalendarIntegrationId">
                                                <MenuItem value={0}>Nenhuma</MenuItem>
                                                {(googleCalendarList || []).map((g) => (
                                                    <MenuItem key={g.id} value={g.id}>
                                                        {g.name} {g.connected ? "(conectado)" : "(pendente OAuth)"}
                                                    </MenuItem>
                                                ))}
                                            </Field>
                                        </FormControl>
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            Após ações no Google Calendar, pode transferir o ticket para uma fila.
                                        </Typography>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>Fila após criar evento</InputLabel>
                                            <Field as={Select} label="Fila após criar evento" name="queueIdAfterCalendarCreate">
                                                <MenuItem value={0}>Não transferir</MenuItem>
                                                {(queues || []).map((q) => (
                                                    <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                                                ))}
                                            </Field>
                                        </FormControl>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>Fila após atualizar evento</InputLabel>
                                            <Field as={Select} label="Fila após atualizar evento" name="queueIdAfterCalendarUpdate">
                                                <MenuItem value={0}>Não transferir</MenuItem>
                                                {(queues || []).map((q) => (
                                                    <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                                                ))}
                                            </Field>
                                        </FormControl>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>Fila após cancelar evento</InputLabel>
                                            <Field as={Select} label="Fila após cancelar evento" name="queueIdAfterCalendarCancel">
                                                <MenuItem value={0}>Não transferir</MenuItem>
                                                {(queues || []).map((q) => (
                                                    <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                                                ))}
                                            </Field>
                                        </FormControl>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion className={classes.accordion}>
                                    <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                                        <Typography className={classes.sectionTitle}>
                                            <Chat />
                                            Configurações de Voz (Opcional)
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails className={classes.accordionDetails}>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>{i18n.t("promptModal.form.voice")}</InputLabel>
                                            <Select
                                                value={values.voice === "texto" ? "texto" : "audio"}
                                                onChange={(e) => {
                                                    if (e.target.value === "texto") {
                                                        setFieldValue("voice", "texto");
                                                        return;
                                                    }
                                                    const provider = inferTtsProvider(values.voice, values.ttsProvider);
                                                    setFieldValue("ttsProvider", provider);
                                                    setFieldValue("voice", provider === "openai" ? "nova" : "pt-BR-FabioNeural");
                                                }}
                                                label={i18n.t("promptModal.form.voice")}
                                            >
                                                <MenuItem value="texto">Apenas Texto</MenuItem>
                                                <MenuItem value="audio">Responder em áudio (nota de voz)</MenuItem>
                                            </Select>
                                        </FormControl>

                                        <FormControlLabel
                                            control={
                                                <Field
                                                    as={Checkbox}
                                                    type="checkbox"
                                                    name="sendAudioTranscriptionToContact"
                                                    color="primary"
                                                />
                                            }
                                            label="Enviar transcrição do áudio do contato no chat"
                                        />
                                        <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
                                            Se ativo, envia uma mensagem &quot;🎤 Sua mensagem de voz: ...&quot; antes da resposta da IA.
                                            A transcrição interna para processar o áudio sempre ocorre.
                                        </Typography>

                                {values.voice !== "texto" && (
                                    <>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>Provedor de voz</InputLabel>
                                            <Field as={Select} name="ttsProvider" label="Provedor de voz"
                                                onChange={(e) => {
                                                    const provider = e.target.value;
                                                    setFieldValue("ttsProvider", provider);
                                                    setFieldValue("voice", provider === "openai" ? "nova" : "pt-BR-FabioNeural");
                                                }}
                                            >
                                                <MenuItem value="azure">Microsoft Azure</MenuItem>
                                                <MenuItem value="openai">OpenAI TTS</MenuItem>
                                            </Field>
                                        </FormControl>

                                        {(values.ttsProvider || "azure") === "azure" ? (
                                            <div className={classes.multFieldLine}>
                                                <FormControl fullWidth margin="dense" variant="outlined">
                                                    <InputLabel>Voz Azure</InputLabel>
                                                    <Field as={Select} name="voice" label="Voz Azure">
                                                        {azureVoices.map((v) => (
                                                            <MenuItem key={v} value={v}>{getVoiceLabel(v)}</MenuItem>
                                                        ))}
                                                    </Field>
                                                </FormControl>
                                                <Field
                                                    as={TextField}
                                                    label={i18n.t("promptModal.form.voiceKey")}
                                                    name="voiceKey"
                                                    error={touched.voiceKey && Boolean(errors.voiceKey)}
                                                    helperText={touched.voiceKey && errors.voiceKey}
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                />
                                                <Field
                                                    as={TextField}
                                                    label={i18n.t("promptModal.form.voiceRegion")}
                                                    name="voiceRegion"
                                                    error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                                                    helperText={touched.voiceRegion && errors.voiceRegion}
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <FormControl fullWidth margin="dense" variant="outlined">
                                                    <InputLabel>Modelo OpenAI TTS</InputLabel>
                                                    <Field as={Select} name="ttsModel" label="Modelo OpenAI TTS">
                                                        {openAiTtsModels.map((m) => (
                                                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                                        ))}
                                                    </Field>
                                                </FormControl>
                                                <FormControl fullWidth margin="dense" variant="outlined">
                                                    <InputLabel>Voz OpenAI</InputLabel>
                                                    <Field as={Select} name="voice" label="Voz OpenAI">
                                                        {openAiTtsVoices.map((v) => (
                                                            <MenuItem key={v} value={v}>{getVoiceLabel(v)}</MenuItem>
                                                        ))}
                                                    </Field>
                                                </FormControl>
                                                <Field
                                                    as={TextField}
                                                    label="Instruções de estilo (opcional)"
                                                    name="openaiTtsInstructions"
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                />
                                                <Field
                                                    as={TextField}
                                                    label="Velocidade"
                                                    name="ttsSpeed"
                                                    type="number"
                                                    inputProps={{ min: 0.25, max: 4, step: 0.05 }}
                                                    variant="outlined"
                                                    margin="dense"
                                                    fullWidth
                                                />
                                            </>
                                        )}

                                        {(() => {
                                            const ttsProvider = values.ttsProvider || "azure";
                                            const catalog =
                                                ttsProvider === "openai"
                                                    ? openAiTtsVoices.map((v) => ({
                                                        value: v,
                                                        label: getVoiceLabel(v),
                                                        ...(openAiVoiceProfiles[v] || { gender: "Neutra", tags: ["versátil"] })
                                                    }))
                                                    : azureVoices.map((v) => ({
                                                        value: v,
                                                        label: getVoiceLabel(v),
                                                        ...(azureVoiceProfiles[v] || { gender: "Neutra", tags: ["versátil"] })
                                                    }));

                                            const playVoicePreview = async (voiceOverride) => {
                                                if (!String(previewText || "").trim()) {
                                                    window.alert("Digite uma frase para testar a voz.");
                                                    return;
                                                }

                                                const selectedVoice = voiceOverride || values.voice;
                                                setPreviewLoadingVoice(selectedVoice);
                                                try {
                                                    const resolvedProvider = inferTtsProvider(selectedVoice, values.ttsProvider);

                                                    await playPreview({
                                                        text: previewText,
                                                        ttsProvider: resolvedProvider,
                                                        voice: selectedVoice,
                                                        voiceKey: values.voiceKey,
                                                        voiceRegion: values.voiceRegion,
                                                        apiKey: values.apiKey,
                                                        ttsModel: values.ttsModel,
                                                        openaiTtsInstructions: values.openaiTtsInstructions,
                                                        ttsSpeed: values.ttsSpeed
                                                    });
                                                } catch (err) {
                                                    window.alert(err?.message || "Falha ao gerar prévia.");
                                                } finally {
                                                    setPreviewLoadingVoice("");
                                                }
                                            };

                                            const downloadVoicePreview = async () => {
                                                if (!String(previewText || "").trim()) {
                                                    window.alert("Digite uma frase para baixar o áudio.");
                                                    return;
                                                }

                                                const selectedVoice = values.voice;
                                                setPreviewLoadingVoice(selectedVoice);
                                                try {
                                                    const resolvedProvider = inferTtsProvider(selectedVoice, values.ttsProvider);
                                                    await downloadPreview({
                                                        text: previewText,
                                                        ttsProvider: resolvedProvider,
                                                        voice: selectedVoice,
                                                        voiceKey: values.voiceKey,
                                                        voiceRegion: values.voiceRegion,
                                                        apiKey: values.apiKey,
                                                        ttsModel: values.ttsModel,
                                                        openaiTtsInstructions: values.openaiTtsInstructions,
                                                        ttsSpeed: values.ttsSpeed
                                                    });
                                                } catch (err) {
                                                    window.alert(err?.message || "Falha ao baixar áudio.");
                                                } finally {
                                                    setPreviewLoadingVoice("");
                                                }
                                            };

                                            return (
                                                <div className={classes.voicePreviewBox}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        <VolumeUp fontSize="small" style={{ verticalAlign: "middle", marginRight: 6 }} />
                                                        Teste e demonstração das vozes
                                                    </Typography>

                                                    <Typography variant="body2" color="textSecondary">
                                                        Use a frase abaixo para testar a voz selecionada ou clique em <b>Ouvir</b> ao lado de qualquer voz da lista.
                                                    </Typography>

                                                    <TextField
                                                        label="Frase de demonstração"
                                                        value={previewText}
                                                        onChange={(e) => setPreviewText(e.target.value)}
                                                        variant="outlined"
                                                        margin="dense"
                                                        fullWidth
                                                        multiline
                                                        rows={2}
                                                        helperText="Essa frase não é salva no prompt; serve apenas para teste."
                                                    />

                                                    <div className={classes.voicePreviewActions}>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            startIcon={<PlayArrow />}
                                                            onClick={() => playVoicePreview(values.voice)}
                                                            disabled={Boolean(previewLoadingVoice)}
                                                        >
                                                            {previewLoadingVoice === values.voice ? "Gerando..." : "Ouvir voz selecionada"}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<Stop />}
                                                            onClick={stopPreview}
                                                        >
                                                            Parar
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            startIcon={<GetApp />}
                                                            onClick={downloadVoicePreview}
                                                            disabled={Boolean(previewLoadingVoice)}
                                                        >
                                                            {previewLoadingVoice === values.voice ? "Baixando..." : "Download áudio"}
                                                        </Button>
                                                    </div>

                                                    <div className={classes.voiceCatalog}>
                                                        <div className={classes.voiceCatalogHeader}>
                                                            <span>Voz</span>
                                                            <span>Perfil</span>
                                                            <span>Etiquetas</span>
                                                            <span>Demonstração</span>
                                                        </div>

                                                        {catalog.map((voiceItem) => (
                                                            <div
                                                                key={voiceItem.value}
                                                                className={classes.voiceCatalogRow}
                                                                style={{
                                                                    backgroundColor:
                                                                        values.voice === voiceItem.value
                                                                            ? "rgba(25, 118, 210, 0.06)"
                                                                            : "transparent"
                                                                }}
                                                            >
                                                                <div>
                                                                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                                        {voiceItem.label}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        {voiceItem.value}
                                                                    </Typography>
                                                                </div>
                                                                <Typography variant="body2">{voiceItem.gender || "Neutra"}</Typography>
                                                                <div className={classes.voiceTags}>
                                                                    {(voiceItem.tags || []).map((tag) => (
                                                                        <Chip
                                                                            key={`${voiceItem.value}-${tag}`}
                                                                            label={tag}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            className={classes.voiceTagChip}
                                                                        />
                                                                    ))}
                                                                </div>
                                                                <div>
                                                                    <Button
                                                                        size="small"
                                                                        variant={values.voice === voiceItem.value ? "contained" : "outlined"}
                                                                        color="primary"
                                                                        startIcon={<PlayArrow />}
                                                                        onClick={() => {
                                                                            setFieldValue("voice", voiceItem.value);
                                                                            playVoicePreview(voiceItem.value);
                                                                        }}
                                                                        disabled={Boolean(previewLoadingVoice)}
                                                                    >
                                                                        {previewLoadingVoice === voiceItem.value ? "Gerando..." : "Ouvir"}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </>
                                )}
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion className={classes.accordion}>
                                    <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                                        <Typography className={classes.sectionTitle}>
                                            <Business />
                                            Transferência por departamento
                                            <Tooltip title="Quando o contato pedir para falar com um departamento, a IA reconhece e transfere.">
                                                <Info fontSize="small" color="action" />
                                            </Tooltip>
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails className={classes.accordionDetails}>
                                        <FieldArray name="departmentTransfers">
                                            {(arrayHelpers) => (
                                                <>
                                                    {(values.departmentTransfers || []).map((item, index) => (
                                                        <div key={index} className={classes.transferRow}>
                                                            <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                                                                <InputLabel>Fila (departamento)</InputLabel>
                                                                <Field as={Select} name={`departmentTransfers.${index}.queueId`} label="Fila (departamento)">
                                                                    <MenuItem value={0}>Selecione a fila</MenuItem>
                                                                    {queues.map((q) => (
                                                                        <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                                                                    ))}
                                                                </Field>
                                                            </FormControl>
                                                            <Field as={TextField} name={`departmentTransfers.${index}.name`} label="Nome do departamento" placeholder="Ex: Comercial" variant="outlined" size="small" style={{ minWidth: 160 }} />
                                                            <Field as={TextField} name={`departmentTransfers.${index}.keywords`} label="Palavras-chave (opcional)" placeholder="comercial, vendas" variant="outlined" size="small" style={{ flex: 1, minWidth: 200 }} helperText="Separadas por vírgula." />
                                                            <IconButton size="small" onClick={() => arrayHelpers.remove(index)} color="secondary" title="Remover">
                                                                <Delete />
                                                            </IconButton>
                                                        </div>
                                                    ))}
                                                    <Button type="button" variant="outlined" size="small" startIcon={<Add />} onClick={() => arrayHelpers.push({ queueId: 0, name: "", keywords: "" })}>
                                                        Adicionar departamento
                                                    </Button>
                                                </>
                                            )}
                                        </FieldArray>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion className={classes.accordion}>
                                    <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                                        <Typography className={classes.sectionTitle}>
                                            <Person />
                                            Transferência para usuário (atendente)
                                            <Tooltip title="Quando o contato pedir para falar com um atendente específico, a IA reconhece e transfere.">
                                                <Info fontSize="small" color="action" />
                                            </Tooltip>
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails className={classes.accordionDetails}>
                                        <FieldArray name="userTransfers">
                                            {(arrayHelpers) => (
                                                <>
                                                    {(values.userTransfers || []).map((item, index) => (
                                                        <div key={index} className={classes.transferRow}>
                                                            <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                                                                <InputLabel>Usuário (atendente)</InputLabel>
                                                                <Field
                                                                    as={Select}
                                                                    name={`userTransfers.${index}.userId`}
                                                                    label="Usuário (atendente)"
                                                                    onChange={(e) => {
                                                                        const uid = Number(e.target.value);
                                                                        const user = usersList.find((u) => u.id === uid);
                                                                        if (user) {
                                                                            setFieldValue(`userTransfers.${index}.userId`, user.id);
                                                                            setFieldValue(`userTransfers.${index}.name`, user.name || "");
                                                                        }
                                                                    }}
                                                                >
                                                                    <MenuItem value={0}>Selecione o usuário</MenuItem>
                                                                    {usersList.map((u) => (
                                                                        <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                                                                    ))}
                                                                </Field>
                                                            </FormControl>
                                                            <Field as={TextField} name={`userTransfers.${index}.name`} label="Nome (reconhecimento)" placeholder="Ex: João" variant="outlined" size="small" style={{ minWidth: 160 }} />
                                                            <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                                                                <InputLabel>Fila do atendente</InputLabel>
                                                                <Field as={Select} name={`userTransfers.${index}.queueId`} label="Fila do atendente">
                                                                    <MenuItem value={0}>Selecione a fila</MenuItem>
                                                                    {queues.map((q) => (
                                                                        <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                                                                    ))}
                                                                </Field>
                                                            </FormControl>
                                                            <Field as={TextField} name={`userTransfers.${index}.keywords`} label="Palavras-chave (opcional)" placeholder="joão, falar com joão" variant="outlined" size="small" style={{ flex: 1, minWidth: 200 }} helperText="Separadas por vírgula." />
                                                            <IconButton size="small" onClick={() => arrayHelpers.remove(index)} color="secondary" title="Remover">
                                                                <Delete />
                                                            </IconButton>
                                                        </div>
                                                    ))}
                                                    <Button type="button" variant="outlined" size="small" startIcon={<Add />} onClick={() => arrayHelpers.push({ userId: 0, name: "", queueId: 0, keywords: "" })}>
                                                        Adicionar usuário
                                                    </Button>
                                                </>
                                            )}
                                        </FieldArray>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion className={classes.accordion}>
                                    <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                                        <Typography className={classes.sectionTitle}>
                                            <LocalOffer />
                                            Tags que a IA pode adicionar ao contato
                                            <Tooltip title="A IA pode marcar o contato com tags permitidas.">
                                                <Info fontSize="small" color="action" />
                                            </Tooltip>
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails className={classes.accordionDetails}>
                                        <FieldArray name="tagsAllowedForAI">
                                            {(arrayHelpers) => (
                                                <>
                                                    {(values.tagsAllowedForAI || []).map((item, index) => (
                                                        <div key={index} className={classes.selectRow}>
                                                            <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
                                                                <InputLabel>Tag</InputLabel>
                                                                <Field
                                                                    as={Select}
                                                                    name={`tagsAllowedForAI.${index}.tagId`}
                                                                    label="Tag"
                                                                    onChange={(e) => {
                                                                        const tag = tagsList.find((t) => t.id === Number(e.target.value));
                                                                        if (tag) {
                                                                            setFieldValue(`tagsAllowedForAI.${index}.tagId`, tag.id);
                                                                            setFieldValue(`tagsAllowedForAI.${index}.name`, tag.name);
                                                                        }
                                                                    }}
                                                                >
                                                                    <MenuItem value={0}>Selecione uma tag</MenuItem>
                                                                    {tagsList.map((tag) => (
                                                                        <MenuItem key={tag.id} value={tag.id}>{tag.name}</MenuItem>
                                                                    ))}
                                                                </Field>
                                                            </FormControl>
                                                            <IconButton size="small" onClick={() => arrayHelpers.remove(index)} color="secondary" title="Remover">
                                                                <Delete />
                                                            </IconButton>
                                                        </div>
                                                    ))}
                                                    <Button type="button" variant="outlined" size="small" startIcon={<Add />} onClick={() => arrayHelpers.push({ tagId: 0, name: "" })}>
                                                        Adicionar tag permitida
                                                    </Button>
                                                </>
                                            )}
                                        </FieldArray>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion className={classes.accordion}>
                                    <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                                        <Typography className={classes.sectionTitle}>
                                            <ViewColumn />
                                            Mover card no Kanban (por conversa)
                                            <Tooltip title="A IA pode mover o card do ticket para lanes permitidas.">
                                                <Info fontSize="small" color="action" />
                                            </Tooltip>
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails className={classes.accordionDetails}>
                                        <FieldArray name="lanesAllowedForAI">
                                            {(arrayHelpers) => (
                                                <>
                                                    {(values.lanesAllowedForAI || []).map((item, index) => (
                                                        <div key={index} className={classes.selectRow}>
                                                            <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
                                                                <InputLabel>Lane do Kanban</InputLabel>
                                                                <Field
                                                                    as={Select}
                                                                    name={`lanesAllowedForAI.${index}.tagId`}
                                                                    label="Lane do Kanban"
                                                                    onChange={(e) => {
                                                                        const lane = lanesList.find((l) => l.id === Number(e.target.value));
                                                                        if (lane) {
                                                                            setFieldValue(`lanesAllowedForAI.${index}.tagId`, lane.id);
                                                                            setFieldValue(`lanesAllowedForAI.${index}.name`, lane.name);
                                                                        }
                                                                    }}
                                                                >
                                                                    <MenuItem value={0}>Selecione uma lane</MenuItem>
                                                                    {lanesList.map((lane) => (
                                                                        <MenuItem key={lane.id} value={lane.id}>{lane.name}</MenuItem>
                                                                    ))}
                                                                </Field>
                                                            </FormControl>
                                                            <IconButton size="small" onClick={() => arrayHelpers.remove(index)} color="secondary" title="Remover">
                                                                <Delete />
                                                            </IconButton>
                                                        </div>
                                                    ))}
                                                    <Button type="button" variant="outlined" size="small" startIcon={<Add />} onClick={() => arrayHelpers.push({ tagId: 0, name: "" })}>
                                                        Adicionar lane permitida
                                                    </Button>
                                                </>
                                            )}
                                        </FieldArray>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion className={classes.accordion}>
                                    <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                                        <Typography className={classes.sectionTitle}>
                                            <AttachFile />
                                            Mídias e links (IA)
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails className={classes.accordionDetails}>
                                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                                            Somente assets cadastrados na biblioteca podem ser enviados pela IA.
                                            A IA usa linhas como &quot;Ação: Enviar imagem: nome_do_asset&quot;.
                                        </Typography>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>Assets permitidos para a IA</InputLabel>
                                            <Select
                                                multiple
                                                value={(values.assetsAllowedForAI || []).map((a) => a.assetId)}
                                                onChange={(e) => {
                                                    const ids = e.target.value;
                                                    const selected = assetsPicker
                                                        .filter((a) => ids.includes(a.id))
                                                        .map((a) => ({ assetId: a.id, name: a.name }));
                                                    setFieldValue("assetsAllowedForAI", selected);
                                                }}
                                                label="Assets permitidos para a IA"
                                                renderValue={(selected) =>
                                                    selected
                                                        .map((id) => assetsPicker.find((a) => a.id === id)?.label || id)
                                                        .join(", ")
                                                }
                                            >
                                                {assetsPicker.map((asset) => (
                                                    <MenuItem key={asset.id} value={asset.id}>
                                                        {asset.label} ({asset.name}) — {asset.type}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Field
                                            as={TextField}
                                            label="Máximo de anexos por resposta"
                                            name="maxAttachmentsPerReply"
                                            type="number"
                                            inputProps={{ min: 1, max: 5 }}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                        <FormControlLabel
                                            control={
                                                <Field
                                                    as={Checkbox}
                                                    type="checkbox"
                                                    name="sendLinksAsTextWhenVoice"
                                                    color="primary"
                                                />
                                            }
                                            label="Enviar links em texto quando a resposta principal for áudio"
                                        />
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            Com áudio: links só são enviados em texto se esta opção estiver ligada (via Ação: ou palavras-chave do asset, ex.: &quot;site da empresa&quot;).
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                                
                                <div className={classes.multFieldLine}>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.temperature")}
                                        name="temperature"
                                        error={touched.temperature && Boolean(errors.temperature)}
                                        helperText={touched.temperature && errors.temperature}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.max_tokens")}
                                        name="maxTokens"
                                        error={touched.maxTokens && Boolean(errors.maxTokens)}
                                        helperText={touched.maxTokens && errors.maxTokens}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.max_messages")}
                                        name="maxMessages"
                                        error={touched.maxMessages && Boolean(errors.maxMessages)}
                                        helperText={touched.maxMessages && errors.maxMessages}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                </div>
                                <Field
                                    as={TextField}
                                    type="number"
                                    inputProps={{ min: 0, max: 120 }}
                                    label={i18n.t("promptModal.form.delay") || "Delay da IA (segundos)"}
                                    name="delay"
                                    error={touched.delay && Boolean(errors.delay)}
                                    helperText={(touched.delay && errors.delay) || (i18n.t("promptModal.form.delayHelp") || "Aguarda este tempo antes de responder (agrupa sequência de mensagens do contato). Padrão: 7")}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("promptModal.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {promptId
                                        ? `${i18n.t("promptModal.buttons.okEdit")}`
                                        : `${i18n.t("promptModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default PromptModal;